import React, { useState } from "react";
import {
  Table,
  Form,
  Dropdown,
  Tab,
  Nav,
  Row,
  Col,
  InputGroup,
  Button,
  Modal,
} from "react-bootstrap";
import ReactPaginate from "react-paginate"; // Import pagination package
import { FaSearch, FaEye, FaEyeSlash, FaUserPlus } from "react-icons/fa";
import TitleComponent from "../TitleComponent/TitleComponent";
import { ICONS } from "../../constants/constants";
import "./TableComponent.css";
import { useDispatch, useSelector } from "react-redux";
import { handleCreateAdmin } from "../../store/actions/userActions";

const TableComponent = ({
  columns,
  data,
  title,
  showSearchBar,
  showFilter,
  showTabs,
  showColors,
  showCount,
  onEdit,
  onDelete,
  onDownload,
  showAddNewAdmin,
  searchplaceholder,

  customClass,
  showRating,
}) => {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ratingFilter, setRatingFilter] = useState("");
  const [activeTab, setActiveTab] = useState(
    title === "Orders" ? "paidOrders" : "approved"
  );


  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(4);
  const [showSendNotification, setShowSendNotification] = useState(false);

  const handleSendNotification = () => setShowSendNotification(true);
  const handleCloseSendNotification = () => setShowSendNotification(false);

  const { user } = useSelector((state) => state.user);
  const userrole = user?.role;
  // remove Actions column entirely for manager role
  const effectiveColumns = (columns || []).filter(
    (c) => !(c === "Actions" && userrole === "manager")
  );
  const tenantId = userrole === "superadmin" ? user?.tenant?.id : null; // Save superAdminId if role is superadmin

  const colorCycle = ["#ffb400", "#7dce13", "#960a0a"];

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleFilterChange = (selectedFilter) => setFilter(selectedFilter);
  const handleStatusFilterChange = (selectedStatus) =>
    setStatusFilter(selectedStatus);
  const handleRatingFilterChange = (selectedRating) =>
    setRatingFilter(selectedRating);

  // Filter data based on search term and active tab
  const filteredData = data?.filter((row) =>
    columns.some(
      (column) =>
        row[column] &&
        row[column].toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );
  const statusFilteredData = filteredData?.filter((row) =>
    statusFilter ? row.Status === statusFilter : true
  );

  const ratingFilteredData = statusFilteredData?.filter((row) =>
    ratingFilter ? row.Rating === ratingFilter : true
  );
  const tabFilteredData = filteredData?.filter((row) => {
    const accountStatus = row?.Status?.toLowerCase();
    const orderStatus = row?.OrderStatus?.toLowerCase();

    // Orders-only tabs
    if (activeTab === "pendingOrders") {
      return orderStatus === "pending";
    }

    if (activeTab === "deliveredOrders") {
      return orderStatus === "delivered";
    }

    if (activeTab === "paidOrders") {
      return orderStatus === "paid";
    }

    if (activeTab === "overdueOrders") {
      return orderStatus === "overdue";
    }

    // Non-Order tabs
    if (activeTab === "approved") {
      return accountStatus === "approved" || orderStatus === "paid";
    }

    if (activeTab === "notapproved") {
      return accountStatus !== "approved" && orderStatus !== "paid";
    }

    return true;
  });





  const paginatedData = tabFilteredData?.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );
  const pageCount = Math.ceil(tabFilteredData?.length / rowsPerPage);

  const handlePageChange = ({ selected }) => setCurrentPage(selected);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(0); // Reset to the first page
  };

  const startRecord = currentPage * rowsPerPage + 1;
  const endRecord = Math.min(
    startRecord + rowsPerPage - 1,
    tabFilteredData?.length
  );

  // State variable for form fields as an object
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    cnic: "",
    address: "",
    tenantId: tenantId,
    company: "",
  });

  const [errors, setErrors] = useState({ email: "", cnic: "" });

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateCNIC = (cnic) => {
    const cnicRegex = /^[0-9]{5}-[0-9]{7}-[0-9]$/;
    return cnicRegex.test(cnic);
  };


  // Dynamic tabs based on title
  const tabs = title === "Orders"
    ? [
      { key: "paidOrders", label: "Paid Orders" },
      { key: "pendingOrders", label: "Pending Orders" },
      { key: "deliveredOrders", label: "Delivered Orders" },
      { key: "overdueOrders", label: "Overdue Orders" },
    ]
    : [
      { key: "approved", label: `Approved ${title}` },
      { key: "notapproved", label: `Pending ${title}` },
    ];


  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission
    let newErrors = { name: "", email: "", cnic: "", address: "", company: "", password: "" };

    // Validate Name
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    // Validate Email
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    // Validate CNIC
    if (!formData.cnic.trim()) {
      newErrors.cnic = "CNIC is required";
    } else if (!validateCNIC(formData.cnic)) {
      newErrors.cnic = "Enter a valid CNIC (e.g. 12345-6789012-3)";
    }

    // Validate Address
    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    // Validate Company
    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
    }

    // Validate Password
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters long";
    }

    if (Object.values(newErrors).some((error) => error)) {
      setErrors(newErrors);
      return;
    }
    const result = await handleCreateAdmin(formData, dispatch);
    if (result.message === "Success") {
      // If the result is successful, refresh the page
      window.location.reload();
    }
    // setFormData({
    //   name: "",
    //   email: "",
    //   password: "",
    //   cnic: "",
    //   address: "",
    //   tenantId: tenantId,
    //   company: "",
    // });
    // setErrors({ email: "", cnic: "" }); // Clear errors after successful submission
  };
  const [showPassword, setShowPassword] = useState(false); // Add this line at the top with other state variables

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev); // Toggle the password visibility
  };

  return (
    <div>
      {data && data.length > 0 ? (
        <>
          {/* Send Notification Modal */}
          <Modal
            className="modal-container custom-modal-width AddModalSuper"
            show={showSendNotification}
            onHide={handleCloseSendNotification}
          >
            <Modal.Header closeButton className="AddModalHeader border-0">
              <Modal.Title className="AddNewAdmin">
                Add New Admin
                <p className="sub-title mt-2 ">Enter Details for new admin</p>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-1">
              <Form onSubmit={handleSubmit}>
                <Row className="adminFormOutliner">
                  {/* Name */}
                  <Col md={6}>
                    <Form.Group controlId="name" className="my-1">
                      <Form.Label className="auth-form-label">
                        Name<span>*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        req
                        placeholder="Enter name"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData({ ...formData, name: e.target.value })
                          setErrors({
                            ...errors,
                            name: '', // Reset the password error
                          });
                        }}
                        //required
                        isInvalid={!!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="address" className="my-1">
                      <Form.Label className="auth-form-label">Address <span>*</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter Admin's full address"
                        value={formData.address}
                        onChange={(e) => {
                          setFormData({ ...formData, address: e.target.value })
                          setErrors({
                            ...errors,
                            address: '', // Reset the password error
                          });
                        }}
                        isInvalid={!!errors.address}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.address}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  {/* Tenant ID */}
                  <Col md={6} className="d-none">
                    <Form.Group controlId="tenantId" className="my-1">
                      <Form.Label>Tenant ID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter tenant ID"
                        value={formData.tenantId}
                        onChange={(e) =>
                          setFormData({ ...formData, tenantId: e.target.value })
                        }
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="adminFormOutliner">
                  {/* Company */}
                  <Col md={6}>
                    <Form.Group controlId="company" className="my-1">
                      <Form.Label className="auth-form-label">
                        Company<span>*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter company name"
                        value={formData.company}
                        onChange={(e) => {
                          setFormData({ ...formData, company: e.target.value })
                          setErrors({
                            ...errors,
                            company: '', // Reset the password error
                          });
                        }}
                        // required
                        isInvalid={!!errors.company}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.company}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  {/* CNIC */}
                  <Col md={6}>
                    <Form.Group controlId="cnic" className="my-1">
                      <Form.Label className="auth-form-label">CNIC<span>*</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter Admin's CNIC (e.g. 12345-6789012-3)"
                        value={formData.cnic}
                        onChange={(e) => {
                          // Only allow digits and dash
                          const formattedValue = e.target.value.replace(/[^0-9-]/g, "");
                          setFormData({ ...formData, cnic: formattedValue });
                          setErrors({
                            ...errors,
                            cnic: "", // Reset error on change
                          });
                        }}
                        isInvalid={!!errors.cnic}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors.cnic}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="adminFormOutliner">
                  {/* Email */}
                  <Col md={6}>
                    <Form.Group controlId="email" className="my-1">
                      <Form.Label className="auth-form-label">
                        Email Address<span>*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Admin's email address here"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value })
                          // Clear password error on change
                          setErrors({
                            ...errors,
                            email: '', // Reset the password error
                          });

                        }}
                        isInvalid={!!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  {/* Password */}
                  <Col md={6}>
                    <Form.Group controlId="password" className="my-1">
                      <Form.Label className="auth-form-label">
                        Password<span>*</span>
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          style={{ borderRight: "none" }}
                          type={showPassword ? "text" : "password"} // Toggle between text and password
                          placeholder="Enter password"
                          value={formData.password}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              password: e.target.value,
                            })

                            // Clear password error on change
                            setErrors({
                              ...errors,
                              password: '', // Reset the password error
                            });
                          }}
                          // required
                          isInvalid={!!errors.password}
                        />

                        <InputGroup.Text
                          onClick={togglePasswordVisibility}
                          style={{
                            cursor: "pointer",
                            borderLeft: "none",
                            background: "transparent",
                            borderLeft: "none", // Keep this outside the condition
                            borderTopRightRadius: "6px",  // Adjust as needed
                            borderBottomRightRadius: "6px",
                            ...(errors.password ? { border: "1px solid red", } : {}),
                          }}
                        >
                          {showPassword ? <FaEye /> : <FaEyeSlash />}{" "}
                          {/* Eye icon for visibility toggle */}
                        </InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {errors.password}
                        </Form.Control.Feedback>
                      </InputGroup>

                    </Form.Group>
                  </Col>
                </Row>
                <Row className="adminFormOutliner">
                  {/* Address */}

                </Row>

                {/* Submit Button */}
                <Form.Group className="text-center mt-4">
                  <Button type="submit" className="button">
                    Add New Admin
                  </Button>
                </Form.Group>
              </Form>
            </Modal.Body>
          </Modal>

          <Row className="align-items-center mb-3 filterWrapper">
            <Col xs={2} md={showFilter ? 4 : 5} sm={5} className="text-start">
              <TitleComponent
                title={showCount ? `${title} (${data?.length})` : title}
              />
            </Col>
            {showAddNewAdmin && (
              <Col xs={9} md={5} sm={5} className="text-end">
                <Button className="addButton" onClick={handleSendNotification}>
                  {ICONS?.addPlusIcon && (
                    <img src={ICONS.addPlusIcon} className="faPlusIcon" />
                  )}
                  Add New Admin
                </Button>
              </Col>
            )}
            {showSearchBar && (
              <>
                <Col
                  className={`d-flex mt-2 ${showFilter
                      ? "justify-content-center searchBarWrapper"
                      : "justify-content-end searchBarWrapper"
                    }`}
                  xs={showFilter ? 4 : 9}
                >
                  <InputGroup className="w-100">
                    <Form.Control
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-input rounded-input"
                      placeholder={searchplaceholder}
                    />



                    {/* 🔍 Search icon */}
                    <InputGroup.Text className="search-icon-wrapper">
                      <FaSearch className="search-icon"
                      />
                    </InputGroup.Text>

                    {/* ❌ Clear button (always visible if there's text) */}
                    {searchTerm && (
                      <button
                        type="button"
                        className="btn btn-link p-0 position-absolute end-0 top-50 translate-middle-y"
                        onClick={() => setSearchTerm("")}
                        style={{
                          border: "none",
                          background: "transparent",
                          fontSize: "1.2rem",
                          color: "#888",
                          zIndex: 5,          // ensures it stays above everything
                          marginRight: "3rem",
                          boxShadow: "none",  // remove shadow
                          textDecoration: "none", // removes underline from btn-link
                        }}
                      >
                        ✕
                      </button>

                    )}
                  </InputGroup>
                </Col>
              </>
            )}
            {showFilter && (
              <Col
                xs={showFilter ? 3 : 4}
                className="d-flex justify-content-end redFarmersDropdown"
              >
                <Dropdown className="dropdown" onSelect={handleFilterChange}>
                  <Dropdown.Toggle
                    className="dropdown-custom-components"
                    id="dropdown-custom-components"
                  >
                    Red Farmers
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end">
                    <Dropdown.Item eventKey="filter1">
                      Yellow Farmers
                    </Dropdown.Item>
                    <Dropdown.Item eventKey="filter2">
                      Red Farmers
                    </Dropdown.Item>
                    <Dropdown.Item eventKey="filter3">
                      Green Farmers
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                {showRating && (
                  <>
                    {/* Status Filter */}
                    <Dropdown
                      onSelect={handleStatusFilterChange}
                      className="ms-2"
                    >
                      <Dropdown.Toggle className="dropdown-custom-components">
                        {statusFilter || "Status"}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item eventKey="">All</Dropdown.Item>
                        <Dropdown.Item eventKey="Approved">
                          Approved
                        </Dropdown.Item>
                        <Dropdown.Item eventKey="Not Approved">
                          Not Approved
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Rating Filter */}
                    <Dropdown
                      onSelect={handleRatingFilterChange}
                      className="ms-2"
                    >
                      <Dropdown.Toggle className="dropdown-custom-components">
                        {ratingFilter || "Rating"}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item eventKey="">All</Dropdown.Item>
                        <Dropdown.Item eventKey="1">1 Star</Dropdown.Item>
                        <Dropdown.Item eventKey="2">2 Stars</Dropdown.Item>
                        <Dropdown.Item eventKey="3">3 Stars</Dropdown.Item>
                        <Dropdown.Item eventKey="4">4 Stars</Dropdown.Item>
                        <Dropdown.Item eventKey="5">5 Stars</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </>
                )}
              </Col>
            )}
          </Row>

          {showTabs && (
            <Tab.Container
              activeKey={activeTab}
              onSelect={(tab) => setActiveTab(tab)}
            >
              {/* <Nav variant="tabs">
                <Nav.Item>
                  <Nav.Link eventKey="approved">
                    {title === "Orders" ? "Confirm " : "Approved "}

                    {title}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="notapproved">Pending {title}</Nav.Link>
                </Nav.Item>
              </Nav> */}

              <Nav variant="tabs">
                {tabs.map((t) => (
                  <Nav.Item key={t.key}>
                    <Nav.Link eventKey={t.key}>{t.label}</Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>

              <Tab.Content>
                <Tab.Pane eventKey={activeTab}>
                  <div className="table-responsive mt-2">
                    <Table hover responsive="sm">
                      <thead>
                        <tr>
    {showColors && (
                            <th style={{ textAlign: "center" }}>
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "10px",
                                  height: "10px",
                                  borderRadius: "50%",
                                  backgroundColor: "black",
                                }}
                              ></span>
                            </th>
                          )}
                          {effectiveColumns.map((column, index) => (
                            <th key={index}>{column}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                          {paginatedData && paginatedData.length > 0 ? (
                          paginatedData.map((row, rowIndex) => (
                            <tr
                              key={rowIndex}
                              className={rowIndex % 2 !== 0 ? 'custom-odd-row' : ''}
                            >
                              {showColors && (
                                <td style={{ textAlign: "center" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: "10px",
                                      height: "10px",
                                      borderRadius: "50%",
                                      backgroundColor: row.farmerColor || "black",
                                    }}
                                  ></span>
                                </td>
                              )}
                              {effectiveColumns.map((column, colIndex) => {
                                const cellContent =
                                  column === "Status" ? (
                                    <span
                                      className={
                                        row[column] === "Approved"
                                          ? "status-active"
                                          : row[column] === "NotApproved"
                                            ? "status-inactive"
                                            : ""
                                      }
                                    >
                                      {row[column] === "Approved"
                                        ? "Active"
                                        : row[column] === "NotApproved"
                                          ? "Inactive"
                                          : row[column]}
                                    </span>
                                  ) : (
                                    row[column]
                                  );

                                return <td key={colIndex}>{cellContent}</td>;
                              })}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={effectiveColumns.length + (showColors ? 1 : 0)}
                              className="text-center"
                            >
                              No Data Found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>

                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          )}

          {!showTabs && (
            <div
              className={`table-responsive ${customClass ? customClass : ""}`}
            >
              <Table striped bordered hover>
                <thead>
                  <tr >
                    {showColors && (
                      <th style={{ textAlign: "center" }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            backgroundColor: "black",
                          }}
                        ></span>
                      </th>
                    )}
                    {columns.map((column, index) => (
                      <th key={index}>{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData?.map((row, rowIndex) => (
                    <tr key={rowIndex}
                      className={rowIndex % 2 !== 0 ? 'custom-odd-row' : ''}>
                      {showColors && (
                        <td style={{ textAlign: "center" }}>
                          <span
                            style={{
                              display: "inline-block",
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              backgroundColor:
                                colorCycle[rowIndex % colorCycle.length],
                            }}
                          ></span>
                        </td>
                      )}
                      {columns.map((column, colIndex) => (
                        <td key={colIndex}>{row[column]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Pagination Controls */}
          <Row className="d-flex align-items-center paginationWrapper">
            <Col
              xs={4}
              className="d-flex align-items-center paginationWrapperPortion"
            >
              <label htmlFor="rowsPerPageSelect">Showing</label>
              <Form.Select
                id="rowsPerPageSelect"
                className="ms-2"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                aria-label="Rows per page"
              >
                <option value={4}>4</option>
                <option value={8}>8</option>
                <option value={12}>12</option>
              </Form.Select>
            </Col>

            <Col xs={5} className="text-center paginationWrapperPortion">
              <span>
                Showing {startRecord} to {endRecord} of{" "}
                {tabFilteredData?.length} records
              </span>
            </Col>

            <Col xs={3} className="d-flex justify-content-end">
              <ReactPaginate
                pageCount={pageCount}
                onPageChange={handlePageChange}
                containerClassName="pagination justify-content-center"
                activeClassName="active"
                previousLabel="<"
                nextLabel=">"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                previousClassName="page-item"
                nextClassName="page-item"
                previousLinkClassName="page-link"
                nextLinkClassName="page-link"
                activeLinkClassName="border border-primary rounded"
                breakLabel="..."
                breakClassName="page-item"
                breakLinkClassName="page-link"
                marginPagesDisplayed={1}
                pageRangeDisplayed={2}
              />
            </Col>
          </Row>
        </>
      ) : (
        // Render message when data is unavailable
        <div>No data available</div>
      )}
    </div>
  );
};

export default TableComponent;
